import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private audioContext: AudioContext | null = null;

  constructor() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.error('AudioContext not supported:', error);
    }
  }

  /**
   * Generate a church bell sound using Web Audio API
   * @param frequency - Bell frequency in Hz
   * @param duration - Duration in seconds
   * @returns Promise that resolves when sound finishes playing
   */
  private createBellSound(frequency: number, duration: number): Promise<void> {
    return new Promise((resolve) => {
      if (!this.audioContext) {
        console.error('AudioContext not available');
        resolve();
        return;
      }

      // Resume audio context if suspended (required by browsers)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      const filterNode = this.audioContext.createBiquadFilter();

      // Connect audio nodes
      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Set oscillator type to create bell-like sound
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);

      // Configure filter for bell-like resonance
      filterNode.type = 'lowpass';
      filterNode.frequency.setValueAtTime(frequency * 2, this.audioContext.currentTime);
      filterNode.Q.setValueAtTime(20, this.audioContext.currentTime);

      // Create envelope (volume curve) for bell sound
      const attackTime = 0.01; // Quick attack
      const decayTime = duration * 0.3; // 30% of duration for decay
      const sustainLevel = 0.3; // Sustain at 30% volume
      const releaseTime = duration * 0.7; // 70% of duration for release

      // Set gain envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.8, this.audioContext.currentTime + attackTime);
      gainNode.gain.exponentialRampToValueAtTime(sustainLevel, this.audioContext.currentTime + decayTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

      // Play the bell sound
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);

      // Resolve promise when sound finishes
      setTimeout(() => {
        oscillator.disconnect();
        resolve();
      }, duration * 1000);
    });
  }

  /**
   * Play a church bell pattern with specified intervals
   * @param intervals - Array of intervals in milliseconds between bell rings
   * @returns Promise that resolves when the entire pattern finishes
   */
  async playBellPattern(intervals: number[]): Promise<void> {
    if (!intervals || intervals.length === 0) {
      return;
    }

    try {
      // Define church bell frequencies (multiple overtones for rich sound)
      const bellFrequencies = [
        220,  // A3 - Fundamental
        440,  // A4 - First harmonic
        660,  // E5 - Second harmonic
        880   // A5 - Third harmonic
      ];

      let cumulativeDelay = 0;

      for (let i = 0; i < intervals.length; i++) {
        const interval = intervals[i];
        
        if (interval && interval > 0) {
          // Play bell sound after cumulative delay
          await this.scheduleBellSound(bellFrequencies, cumulativeDelay);

          // Add current interval to cumulative delay (convert to milliseconds)
          cumulativeDelay += (interval * 1000)+300;
        }
        
      }
      await this.scheduleBellSound(bellFrequencies, cumulativeDelay);

      // Wait for the entire pattern to complete
      await new Promise(resolve => setTimeout(resolve, cumulativeDelay + 1500));
    } catch (error) {
      console.error('Error playing bell pattern:', error);
    }
  }
  
  async scheduleBellSound(bellFrequencies: number[], cumulativeDelay: number): Promise<void> {
    setTimeout(async () => {
        // Play multiple frequencies simultaneously for rich bell sound
        const bellPromises = bellFrequencies.map(freq => 
          this.createBellSound(freq, 3) // 1.5 second bell sound
        );
        await Promise.all(bellPromises);
      }, cumulativeDelay);
  }

  /**
   * Play a simple single bell ring for testing
   * @param frequency - Bell frequency (default: 220Hz)
   * @param duration - Duration in seconds (default: 1.5s)
   */
  async playSingleBell(frequency: number = 220, duration: number = 1.5): Promise<void> {
    await this.createBellSound(frequency, duration);
  }

  /**
   * Stop all currently playing audio
   */
  stopAll(): void {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        // Recreate AudioContext for future use
        try {
          this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        } catch (error) {
          console.error('Failed to recreate AudioContext:', error);
        }
      });
    }
  }
}
